import { Helpers } from "@flexday/common-react"
import { initializeApp } from "firebase/app"
import { getAuth } from "firebase/auth"
import {
  getDoc,
  doc,
  getFirestore,
  updateDoc,
  deleteField,
} from "firebase/firestore"
import { getFunctions, httpsCallable } from "firebase/functions"
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { isPast, isToday } from "date-fns"
import { Utils } from "@flexday/common"
import { update, trackEvent } from "@intercom/messenger-js-sdk"
import ReservationInfo from "./reservation-info"
import AccountInfo from "./account-info"
import OrderSummary from "./order-summary"
import Button from "../reusable-components/button"
import {
  formatCurrency,
  formatDiscount,
} from "../booking-configurator/formatters"

const CartPreview = ({ currency }) => {
  const { getFirebaseConfig } = Helpers
  const firebaseConfig = getFirebaseConfig({
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_FIREBASE_MESSAGE_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  })
  const app = initializeApp(firebaseConfig)
  const firestore = getFirestore(app)
  const auth = getAuth(app)

  const [loading, setLoading] = useState(true)
  const [isSubmittingForm, setIsSubmittingForm] = useState(false)
  const [cart, setCart] = useState({})
  const [inventoryPageSnapshots, setInventoryPageSnapshots] = useState([])
  const cartId = window.localStorage.getItem("cartId")

  const [promoCode, setPromoCode] = useState()

  const cartTotalBeforeDiscount = inventoryPageSnapshots.reduce(
    (sum, snapshot) => {
      const inventoryPage = snapshot.data()
      const inventoryInCart = cart[snapshot.ref.id]
      const quantity = inventoryInCart.quantity
      const { amountInCents } =
        inventoryPage.prices.onDemand[currency.toLowerCase()]

      const price = amountInCents * quantity
      const discount =
        inventoryInCart.discount && inventoryPage.discount
          ? inventoryPage.discount
          : 0

      const totalPrice = price - discount * price
      return sum + totalPrice
    },
    0
  )

  const orderDiscount =
    promoCode?.unit === "percentage"
      ? promoCode.amount * cartTotalBeforeDiscount
      : 0

  const cartTotal = cartTotalBeforeDiscount - orderDiscount

  // Order summary states
  const [showLoggedOutError, setShowLoggedOutError] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      email: auth.currentUser?.email ?? "",
      name: auth.currentUser?.displayName ?? "",
    },
  })

  const cartAnalyticsEvent = (cart, inventoryPageSnapshots) => {
    if (inventoryPageSnapshots.length < 1) {
      return {}
    }

    const inventoryPage = inventoryPageSnapshots[0].data()
    const {
      name,
      images,
      address,
      inventory: { id: inventoryId },
    } = inventoryPage
    const { amountInCents } =
      inventoryPage.prices.onDemand[currency.toLowerCase()]
    const inventoryPageId = inventoryPageSnapshots[0].id
    const { data, quantity } = cart[inventoryPageId]
    const slug = Utils.slugifyName(name)
    const url = `${process.env.REACT_APP_FLEXDAY_URL}/suite/${slug}`
    const inventoriesCount = Object.keys(cart).filter(
      (key) => key !== "createdAt"
    ).length
    const additionalNote =
      inventoriesCount > 2
        ? `Plus, ${inventoriesCount - 1} more workspaces...`
        : inventoriesCount === 2
        ? `Plus, 1 more workspace...`
        : ""

    return {
      "Inventory Name": name,
      "Inventory Address": address,
      "Inventory Image": `https://us-central1-flexday-6dacf.cloudfunctions.net/inventory-getImage?inventoryId=${inventoryId}`,
      "Inventory Url": url,
      "Selected Dates": data.dates.sort().join(", "),
      "Inventory Price": formatCurrency(amountInCents, currency),
      "Inventory Quantity": quantity,
      "Inventory Subtotal": formatCurrency(amountInCents * quantity, currency),
      "Cart Total": formatCurrency(cartTotal, currency),
      "Additional Inventory Note": additionalNote,
      "Request Date": Utils.getRequestDate(),
    }
  }

  useEffect(() => {
    reset({
      email: auth.currentUser?.email ?? "",
      name: auth.currentUser?.displayName ?? "",
    })
  }, [auth.currentUser])

  useEffect(() => {
    async function getCart() {
      // Fetch cart from Firestore
      const snapshot = await getDoc(doc(firestore, "carts", cartId))
      const cart = snapshot.data() || {}

      setCart(cart)

      const inventoryPageIds = Object.keys(cart || {}).filter(
        (_) => _ !== "createdAt"
      )

      // Fetch inventory pages from Firestore
      const inventoryPageSnapshots = await Promise.all(
        inventoryPageIds.map((inventoryPageId) =>
          getDoc(doc(firestore, "inventoryPages", inventoryPageId))
        )
      )

      setInventoryPageSnapshots(inventoryPageSnapshots)

      await trackEvent(
        "User Viewed Cart v1",
        cartAnalyticsEvent(cart, inventoryPageSnapshots)
      )

      setLoading(false)
    }

    if (cartId) {
      getCart()
    } else {
      setLoading(false)
    }
  }, [cartId, firestore])

  const onRemove = async (inventoryPageId) => {
    await updateDoc(doc(firestore, "carts", cartId), {
      [inventoryPageId]: deleteField(),
    })
    const snapshot = await getDoc(doc(firestore, "carts", cartId))

    setInventoryPageSnapshots((inventoryPageSnapshots) =>
      inventoryPageSnapshots.filter((_) => _.ref.id !== inventoryPageId)
    )
    setCart(snapshot.data())
  }

  const itemsInCart = Object.keys(cart || {}).filter((_) => _ !== "createdAt")

  if ((cart && itemsInCart.length > 0 && !inventoryPageSnapshots) || loading) {
    return <p>Loading...</p>
  }

  const submitOrder = async ({ name, email }) => {
    setIsSubmittingForm(true)
    await update({ name, email })
    setTimeout(() => {
      trackEvent(
        "User Tapped Cart CTA v1",
        cartAnalyticsEvent(cart, inventoryPageSnapshots)
      )
    }, 300)

    // Check for past dates in order
    for (const snapshot of inventoryPageSnapshots) {
      const inventoryInCart = cart[snapshot.ref.id]
      const sortedDates = inventoryInCart.data.dates.sort()
      const firstDate = new Date(sortedDates.at(0))
      if (isPast(firstDate) && !isToday(firstDate)) {
        window.alert(
          "It looks like your order contains bookings from the past. Please remove them from your order and re-submit."
        )
        setIsSubmittingForm(false)
        return
      }
    }

    const functions = getFunctions()
    const submitOrder = httpsCallable(functions, "submitOrder-submitOrder")

    try {
      setShowLoggedOutError(false)
      const result = await submitOrder({
        data: {
          cartId,
          email,
          name,
          couponId: promoCode?.couponId,
          isLoggedIn: !!auth.currentUser,
          currency: currency.toLowerCase(),
        },
      })
      const invoiceUrl = result.data

      setIsSubmittingForm(false)
      window.location.href = invoiceUrl
    } catch (error) {
      if (error.code === "functions/unauthenticated") {
        setShowLoggedOutError(true)
        return
      }
      window.alert(
        "Your order failed to process. You have not been charged. Please try again later."
      )
    } finally {
      setIsSubmittingForm(false)
    }
  }

  return (
    <div className="">
      <form
        onSubmit={handleSubmit(submitOrder)}
        className="flex flex-col md:flex-row gap-4">
        <div className="flex flex-col gap-8 md:gap-4 pb-8 border-b md:border-b-0 md:border-r md:pr-8 border-slate-200">
          <ReservationInfo
            cart={cart}
            inventoryPageSnapshots={inventoryPageSnapshots}
            onRemove={onRemove}
          />
          <AccountInfo auth={auth} register={register} />
        </div>

        <div className="pt-4 flex flex-col gap-4">
          <OrderSummary
            cart={cart}
            inventoryPageSnapshots={inventoryPageSnapshots}
            firestore={firestore}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            orderCurrency={currency}
            orderDiscount={orderDiscount}
            cartTotal={cartTotal}
          />

          <Button
            disabled={itemsInCart.length === 0}
            title="Submit Order"
            className={
              "!w-44 px-10 !bg-[#652cb3] self-center rounded-lg hover:opacity-75 transition-opacity"
            }
            loading={isSubmittingForm || isSubmitting}
          />

          {showLoggedOutError && (
            <p className="text-sm text-red-500 self-center">
              It looks like there's already an account associated to this email.
              Please log in to place your order.
            </p>
          )}

          <div className="text-xs text-slate-500 italic text-center">
            <p>* Taxes will be applied at checkout.</p>
            <p>Please review your reservation summary carefully.</p>
            <p>
              A Stripe receipt and personalized order confirmation from our
              Convierge Team will be sent to your email address on record.
            </p>
          </div>

          <div className="flex flex-col gap-6 mt-4">
            <p className="font-bold">Thank you for using Flexday.</p>

            <p>
              <span className="font-bold">
                Reschedule / Cancellation Policy:
              </span>{" "}
              Please provide 10 business days' notice if you need to reschedule
              or cancel your reservation. Flexday will always do our best to
              accommodate.
            </p>

            <p>
              <span className="font-bold">Payment Terms:</span> For all
              reservations totalling less than 3 months' room use, Flexday
              requires full payment upfront. For bookings that are longer,
              payment can be separated into 3-month installments.
            </p>

            <p>
              Email{" "}
              <a href="mailto:concierge@flexday.com" className="text-[#652cb3]">
                concierge@flexday.com
              </a>{" "}
              for any of these requests.
            </p>
          </div>
        </div>
      </form>
    </div>
  )
}

export default CartPreview
